import BaseStore from '@/store/BaseStore'
import { Module, Mutation } from '@/node_modules/vuex-module-decorators'
import {
  defaultUserSearchConditionsItem,
  UserSearchConditionsItem
} from '@/components/organisms/UserSearchCondition'
import { cloneDeep, isEqual, reject } from 'lodash'
import {
  defaultUserSearchTableItem,
  UserSearchTableItem
} from '@/components/organisms/UserSearchTable'
import { Action } from 'vuex-module-decorators'
import { SearchUser, SearchUsersRequest, userApi } from '@/api'
import {
  ITheSideNavigationUserItem,
  defaultNavigationUserItem
} from '@/components/organisms/TheSideNavigationUser'
import { UserOpenedItem } from '@/pages/user.vue'
import { authUserStore, userStore } from '@/utils/store-accessor'

@Module({
  name: 'UserStore',
  namespaced: true,
  stateFactory: true
})
export default class UserStore extends BaseStore {
  _isPagination: boolean = false

  _searchResult: UserSearchTableItem = defaultUserSearchTableItem()

  _searchConditions: UserSearchConditionsItem = defaultUserSearchConditionsItem()

  _searchedConditions: UserSearchConditionsItem = defaultUserSearchConditionsItem()

  _displayOldIdAlert: boolean = false

  _openedNavigationItem: ITheSideNavigationUserItem = defaultNavigationUserItem()

  _pinedNavigationItems: ITheSideNavigationUserItem[] = []

  get isPagination(): boolean {
    return this._isPagination
  }

  get searchResult(): UserSearchTableItem {
    return this._searchResult
  }

  get searchConditions(): UserSearchConditionsItem {
    return this._searchConditions
  }

  get searchedConditions(): UserSearchConditionsItem {
    return this._searchedConditions
  }

  get displayOldIdAlert(): boolean {
    return this._displayOldIdAlert
  }

  get openedNavigationItem(): ITheSideNavigationUserItem {
    return this._openedNavigationItem
  }

  get pinedNavigationItems(): ITheSideNavigationUserItem[] {
    return this._pinedNavigationItems
  }

  @Mutation
  public reset() {
    this._isPagination = false
    this._searchResult = defaultUserSearchTableItem()
    this._searchConditions = defaultUserSearchConditionsItem()
    this._searchedConditions = defaultUserSearchConditionsItem()
    this._displayOldIdAlert = false
    this._openedNavigationItem = defaultNavigationUserItem()
  }

  @Mutation
  public setIsPagination(value: boolean) {
    this._isPagination = value
  }

  @Mutation
  public setSearchResult(value: UserSearchTableItem) {
    this._searchResult = cloneDeep(value)
  }

  @Mutation
  public resetSearchResult() {
    this._searchResult = defaultUserSearchTableItem()
  }

  @Mutation
  public setSearchConditions(value: UserSearchConditionsItem) {
    this._searchConditions = cloneDeep(value)
  }

  @Mutation
  public setSearchedConditions(value: UserSearchConditionsItem) {
    this._searchedConditions = cloneDeep(value)
  }

  @Mutation
  public resetSearchedConditions() {
    this._searchedConditions = defaultUserSearchConditionsItem()
  }

  @Mutation
  public setDisplayOldIdAlert(value: boolean) {
    this._displayOldIdAlert = value
  }

  @Mutation
  public setOpenedNavigationItem(value: ITheSideNavigationUserItem) {
    this._openedNavigationItem = value
  }

  @Mutation
  public resetOpenedNavigationItem() {
    this._openedNavigationItem = defaultNavigationUserItem()
  }

  @Mutation
  public setPinedNavigationItems(value: ITheSideNavigationUserItem[]) {
    this._pinedNavigationItems = value
  }

  @Mutation
  public loadPinedNavigationItems() {
    const value = localStorage.getItem(
      'user-pined-items_' + authUserStore.authUser.id
    )
    // this.debug('loadPinedItems', value)
    const items: UserOpenedItem[] = value ? JSON.parse(value) : []

    userStore.setPinedNavigationItems(
      items.map(item => ({
        id: item.id,
        title: item.title,
        key: this._pinedNavigationItems.length,
        url: `/user/${item.id}?title=${item.title}&pined=true`,
        icon: '',
        depth: 2,
        canRemove: true,
        mini: {
          title: item.title ? item.title[0] : '',
          icon: ''
        }
      }))
    )
  }

  @Action({ rawError: true })
  public removePinedNavigationItems(id: string) {
    const value = localStorage.getItem(
      'user-pined-items_' + authUserStore.authUser.id
    )
    let items = value ? JSON.parse(value) : []
    items = reject(items, { id })

    localStorage.setItem(
      'user-pined-items_' + authUserStore.authUser.id,
      JSON.stringify(items)
    )

    this.loadPinedNavigationItems()
  }

  @Action({ rawError: true })
  public async searchUsers(
    request: SearchUsersRequest
  ): Promise<UserSearchTableItem> {
    return Object.assign(defaultUserSearchTableItem(), {
      items: (await this.searchUsersInterface(request)).map(
        (result, index) => ({
          isSelected: index === 0,
          ...result
        })
      )
    })
  }

  @Action({ rawError: true })
  public async researchUsers() {
    if (isEqual(this.searchedConditions, defaultUserSearchConditionsItem())) {
      return
    }

    this.setSearchResult(
      Object.assign(defaultUserSearchTableItem(), {
        sortColumn: this._searchResult.sortColumn,
        scrollTop: this._searchResult.scrollTop,
        items: (
          await this.searchUsersInterface({
            name: this.searchedConditions.name.input,
            id: this.searchedConditions.id.input,
            birthday: this.searchedConditions.birthday.input,
            phoneNumber: this.searchedConditions.phoneNumber.input,
            address: this.searchedConditions.address.input,
            mailAddress: this.searchedConditions.mailAddress.input,
            gender: this.searchedConditions.gender.input.code,
            userType: this.searchedConditions.userType.input.code,
            status: this.searchedConditions.status.input.code,
            areaCode: this.searchedConditions.areaCode.input.code,
            workType: this.searchedConditions.workType.input.code,
            studentGrade: this.searchedConditions.studentGrade.input.code,
            studentClass: this.searchedConditions.studentClass.input.code,
            studentNumber: this.searchedConditions.studentNumber.input
          })
        ).map((result, index) => ({
          isSelected: index === 0,
          ...result
        }))
      })
    )
  }

  @Action({ rawError: true })
  private searchUsersInterface(
    request: SearchUsersRequest
  ): Promise<SearchUser[]> {
    return userApi.searchUsers(request)
  }
}
